import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import API_BASE_URL from 'src/services/api.config';
import { useAllOrganizations } from 'src/hooks/useAllOrganizations';
import FlowCustomContent, {
  type FlowDesignColorPalette,
  type FlowDesignTranslations,
} from '../../FlowCustom/FlowCustomContent';
import {
  appendFlowDesignFormData,
  defaultFlowDesignForm,
  type FlowDesignForm,
} from '../flow-design.form';
import {
  defaultFlowDocumentTypes,
  type NewFlowWizardState,
} from '../new-flow-wizard.state';

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Deep-merge plain objects (translations trees). */
function deepMergePlain(
  base: Record<string, unknown> | undefined,
  patch: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!patch) return { ...(base ?? {}) };
  const out: Record<string, unknown> = { ...(base ?? {}) };
  for (const key of Object.keys(patch)) {
    const pv = patch[key];
    const bv = out[key];
    if (isPlainRecord(pv) && isPlainRecord(bv)) {
      out[key] = deepMergePlain(bv, pv);
    } else if (pv !== undefined) {
      out[key] = pv;
    }
  }
  return out;
}

/** Flow Design params for PUT api/v1/flows?flowId= (form-data) — see flow-design.form.ts */
const extractBgHex = (cls: string | undefined): string | undefined =>
  cls?.match(/bg-\[([^\]]+)\]/)?.[1];
const extractTextHex = (cls: string | undefined): string | undefined =>
  cls?.match(/text-\[([^\]]+)\]/)?.[1];

type BannerImageKv = { Key: string; Value: any };
type BannerImageEntry = BannerImageKv[];

const getBannerUrlFromBannerImage = (
  bannerImage: unknown,
  route: string,
): string | null => {
  if (!Array.isArray(bannerImage)) return null;

  const entries = bannerImage as BannerImageEntry[];
  const getRoute = (e: BannerImageEntry) =>
    e?.find(kv => kv?.Key === 'route')?.Value;
  const getBanner = (e: BannerImageEntry) =>
    e?.find(kv => kv?.Key === 'banner')?.Value;

  const pickUrlFromBanner = (bannerVal: unknown): string | null => {
    if (!Array.isArray(bannerVal)) return null;
    const kvs = bannerVal as BannerImageKv[];
    const byKey = (k: string) => kvs.find(x => x?.Key === k)?.Value;
    const en = byKey('en');
    const de = byKey('de');
    const any = kvs.find(x => typeof x?.Value === 'string' && x.Value)?.Value;
    const chosen = (en || de || any) as unknown;
    return typeof chosen === 'string' && chosen.trim() ? chosen.trim() : null;
  };

  const exact = entries.find(e => getRoute(e) === route);
  const star = entries.find(e => getRoute(e) === '*');
  const first = entries[0];

  return (
    pickUrlFromBanner(exact ? getBanner(exact) : null) ||
    pickUrlFromBanner(star ? getBanner(star) : null) ||
    pickUrlFromBanner(first ? getBanner(first) : null)
  );
};

/** Keys that map to a visible region in the phone preview (for highlight indicators). */
export type FlowDesignFieldKey =
  | 'appBarClass'
  | 'appBarButtonColor'
  | 'buttonBgColor'
  | 'buttonTextColor'
  | 'documentLinkBgColor'
  | 'allowedIdsColor'
  | 'allowedIdsTextColor'
  | 'backgroundColor'
  | 'textColor'
  | 'primaryTextColor'
  | 'secondaryButtonTextColor'
  | 'appBarLogoFile'
  | 'progressBarTrackColor'
  | 'progressBarFillColor';

// interface CheckOption {
//   value: string;
//   label: string;
// }

interface DataMatchField {
  active: boolean;
  service: string[];
  limits: {
    fail: number;
    review: number;
  };
}

interface CheckSettings {
  persondatamatch: boolean;
  addressdatamatch: boolean;
  continueonfail: boolean;
  continueonreview: boolean;
  continueon: string[];
  retryonfail: number;
  retryonreview: number;
  datamatchlimits: {
    fail: number;
    review: number;
  };
  datamatch: {
    matchname: DataMatchField;
    matchfirstname: DataMatchField;
    matchlastname: DataMatchField;
    matchdob: DataMatchField;
    matchaddress: DataMatchField;
  };
  validation: {
    address: {
      active: boolean;
    };
  };
}

interface Check {
  type: string;
  provider: string;
  conditions: any;
  checkoptions: string[][];
  manual: boolean;
  settings: CheckSettings;
}

/** Design config matching flowConfig.theme / appBar / documentsList (for layout) */
export interface FlowDesignConfig {
  appBar: {
    appBarLogo: string;
    appBarClass: string;
    appBarButtonColor: string;
    appBarTitleClass: string;
  };
  theme: {
    buttonClass: string;
    secondaryButtonClass: string;
    documentLinkClass: string;
    progressBarClass: string;
    backgroundColor: string;
    textColor: string;
    fonts: {
      primaryFont: [string, string];
      subtitleFont: [string, string];
      titleFont: [string, string];
    };
    color?: Partial<FlowDesignColorPalette>;
  };
  consentMessage: string;
  documents: Array<{
    type: string;
    path: string;
    color: string;
    textColor: string;
  }>;
  translations?: FlowDesignTranslations;
}

const defaultDesignConfig: FlowDesignConfig = {
  appBar: {
    appBarLogo: '',
    appBarClass: 'bg-[#00387D]',
    appBarButtonColor: '#ffffff',
    appBarTitleClass: '',
  },
  theme: {
    buttonClass:
      'rounded-full text-[#ffffff] mb-6 py-3 px-8 font-semibold bg-[#00387D]',
    secondaryButtonClass: 'text-center flex m-auto text-[15px] text-[#00387D]',
    documentLinkClass: 'bg-[#00387D] border-solid border-2 rounded-lg',
    progressBarClass: '',
    backgroundColor: '#ffffff',
    textColor: '#000000',
    fonts: {
      primaryFont: ['SourceSansPro', 'sans-serif'],
      subtitleFont: ['Source', 'sans-serif'],
      titleFont: ['Ubuntu', 'sans-serif'],
    },
    color: {
      appBarBackgroundColor: '#00387D',
      primaryBgColor: '#ffffff',
      primaryTextColor: '#000000',
      loadingSpinnerColor: '#02bd9c',
      loadingSpinnerFill: '#02bd9c',
      buttonBgColor: '#00387D',
      buttonTextColor: '#ffffff',
      progressBarBgColor: '#39f56a',
      docSelectContainerBgColor: '#ffffff',
      accentColor: '#39f56a',
      sdkSpecialTextColor: '#000000',
      incodeTextColor: '#000000',
      incodeHighlightedTextColor: '#000000',
      abortButtonColor: '#00387D',
      appBarTitleColor: '#ffffff',
      cameraHeaderBgColor: '#000000',
      tutorialAccentColor: '#000000',
      ocrFieldsBgColor: '#ffffff',
      ocrInputTextColor: '#ffffff',
      reasonActiveBgColor: '#c0f9c8',
      reasonHoverBgColor: '#c0f9c8',
      modalBgColor: '#ffffff',
      docSelectBgColor: '#00387D',
      themeSecondaryTextColor: '#00387D',
      themeErrorColor: '#c23b21',
      themeRadioColor: '#c0f9c8',
      themeRadioAccentColor: '#89379F',
      checkboxAccentColor: '#000000',
    },
  },
  consentMessage:
    'I hereby consent to processing my biometric data for the purpose of verifying my identity.',
  documents: [
    { type: 'ID', path: 'id', color: '#ffffff', textColor: '#ffffff' },
    { type: 'GermanID', path: 'id', color: '#ffffff', textColor: '#ffffff' },
    {
      type: 'Passport',
      path: 'passport',
      color: '#ffffff',
      textColor: '#ffffff',
    },
  ],
};

interface FlowConfiguration {
  customerid: string;
  flowName: string;
  checks: Check[];
  externalids: {
    incode: string;
  };
  documentTypes: {
    id: boolean;
    passport: boolean;
    residence: boolean;
    drivingLicense: boolean;
  };
  design: FlowDesignConfig;
  apiversion: string;
}

const getInitialConfiguration = (): FlowConfiguration => ({
  customerid: '649025e334a855cf37cf58c7',
  flowName: '',
  checks: [],
  externalids: { incode: '' },
  documentTypes: {
    id: false,
    passport: false,
    residence: false,
    drivingLicense: false,
  },
  design: defaultDesignConfig,
  apiversion: '4',
});

const FlowConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flowId') ?? '';
  const wizardState = location.state as NewFlowWizardState | null;

  const [configuration, setConfiguration] = useState<FlowConfiguration>(() => {
    const initial = getInitialConfiguration();
    if (!wizardState) return initial;
    const hasIdv = wizardState.selectedProducts?.includes('idv');
    return {
      ...initial,
      flowName: wizardState.flowName ?? initial.flowName,
      customerid: wizardState.customerId ?? initial.customerid,
      externalids: {
        incode: wizardState.incodeClientId ?? initial.externalids.incode,
      },
      documentTypes:
        wizardState.documentTypes ??
        (hasIdv ? defaultFlowDocumentTypes : initial.documentTypes),
    };
  });
  const [selectedProducts] = useState<string[]>(
    wizardState?.selectedProducts ?? [],
  );
  const [poaDocumentTypes] = useState(wizardState?.poaDocumentTypes ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const [loadFlowError, setLoadFlowError] = useState<string | null>(null);
  const [flowDesign, setFlowDesign] = useState<FlowDesignForm>(() =>
    wizardState?.flowDesign
      ? { ...defaultFlowDesignForm, ...wizardState.flowDesign }
      : defaultFlowDesignForm,
  );

  const flowDesignLogoUrl = useMemo(() => {
    if (!flowDesign.appBarLogoFile) return null;
    return URL.createObjectURL(flowDesign.appBarLogoFile);
  }, [flowDesign.appBarLogoFile]);
  useEffect(() => {
    return () => {
      if (flowDesignLogoUrl) URL.revokeObjectURL(flowDesignLogoUrl);
    };
  }, [flowDesignLogoUrl]);

  // Load flow details when editing (flowId in URL)
  useEffect(() => {
    if (!flowId) {
      setLoadFlowError(null);
      return;
    }
    let cancelled = false;
    setLoadingFlow(true);
    setLoadFlowError(null);
    axios
      .get(`${API_BASE_URL}/flows/${encodeURIComponent(flowId)}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then(response => {
        if (cancelled) return;
        const data = response?.data ?? {};
        setConfiguration(prev => {
          const design =
            data.design && typeof data.design === 'object'
              ? { ...defaultDesignConfig, ...data.design }
              : prev.design;
          const documentTypes = { ...prev.documentTypes };
          if (data.documentTypes && Array.isArray(data.documentTypes)) {
            const arr = data.documentTypes as string[];
            documentTypes.id = arr.includes('ID');
            documentTypes.passport = arr.includes('PASSPORT');
            documentTypes.residence = arr.includes('RESIDENCE');
            documentTypes.drivingLicense = arr.includes('DRIVINGLICENSE');
          } else if (typeof data.checkOptions === 'string') {
            // Fallback for payloads like: "document:ID,PASSPORT,RESIDENCE:incode"
            const [, docsPart] = data.checkOptions.split(':');
            const docs = (docsPart || '')
              .split(',')
              .map((d: string) => d.trim().toUpperCase())
              .filter(Boolean);
            documentTypes.id = docs.includes('ID');
            documentTypes.passport = docs.includes('PASSPORT');
            documentTypes.residence = docs.includes('RESIDENCE');
            documentTypes.drivingLicense = docs.includes('DRIVINGLICENSE');
          }
          const flat = data?.design ?? data;
          const hex = (v: unknown) =>
            typeof v === 'string' && /^#?[0-9A-Fa-f]{3,8}$/.test(v)
              ? v.startsWith('#')
                ? v
                : `#${v}`
              : undefined;
          const themeColor: Partial<FlowDesignColorPalette> | undefined =
            flat?.theme?.color ?? flat?.color ?? undefined;
          setFlowDesign({
            appBarLogo:
              typeof flat?.appBarLogo === 'string'
                ? flat.appBarLogo
                : typeof (flat?.appBar as { appBarLogo?: string })
                      ?.appBarLogo === 'string'
                  ? (flat.appBar as { appBarLogo: string }).appBarLogo
                  : undefined,
            appBarLogoFile: null,
            bannerImg:
              getBannerUrlFromBannerImage(
                (flat as any)?.bannerImage ?? (flat as any)?.theme?.bannerImage,
                'clientId-document-select',
              ) ?? null,
            bannerImageFile: null,
            appBarClass:
              hex(flat?.appBarClass) ??
              extractBgHex(flat?.appBarClass) ??
              extractBgHex(flat?.appBar?.appBarClass) ??
              defaultFlowDesignForm.appBarClass,
            buttonBgColor:
              hex(flat?.buttonBgColor) ??
              extractBgHex(flat?.buttonClass) ??
              extractBgHex(flat?.theme?.buttonClass) ??
              defaultFlowDesignForm.buttonBgColor,
            buttonTextColor:
              hex(flat?.buttonTextColor) ??
              extractTextHex(flat?.buttonClass) ??
              extractTextHex(flat?.theme?.buttonClass) ??
              defaultFlowDesignForm.buttonTextColor,
            secondaryButtonTextColor:
              hex(flat?.secondaryButtonTextColor) ??
              extractTextHex(flat?.secondaryButtonClass) ??
              extractTextHex(flat?.theme?.secondaryButtonClass) ??
              defaultFlowDesignForm.secondaryButtonTextColor,
            documentLinkBgColor:
              hex(flat?.documentLinkBgColor) ??
              extractBgHex(flat?.documentLinkClass) ??
              extractBgHex(flat?.theme?.documentLinkClass) ??
              defaultFlowDesignForm.documentLinkBgColor,
            // Some APIs return allowed-id colors on the first document entry.
            // Fallback to that shape when top-level fields are missing.
            allowedIdsColor:
              hex(flat?.allowedIdsColor) ??
              hex(flat?.documents?.[0]?.color) ??
              defaultFlowDesignForm.allowedIdsColor,
            allowedIdsTextColor:
              hex(flat?.allowedIdsTextColor) ??
              hex(flat?.documents?.[0]?.textColor) ??
              defaultFlowDesignForm.allowedIdsTextColor,
            appBarButtonColor:
              hex(flat?.appBarButtonColor) ??
              hex(flat?.appBar?.appBarButtonColor) ??
              defaultFlowDesignForm.appBarButtonColor,
            backgroundColor:
              hex(flat?.backgroundColor) ??
              hex(flat?.theme?.backgroundColor) ??
              defaultFlowDesignForm.backgroundColor,
            textColor:
              hex(flat?.textColor) ??
              hex(flat?.theme?.textColor) ??
              defaultFlowDesignForm.textColor,
            primaryTextColor:
              hex(flat?.primaryTextColor) ??
              defaultFlowDesignForm.primaryTextColor,
            progressBarTrackColor:
              hex(flat?.progressBarTrackColor ?? flat?.progressBarOuterColor) ??
              defaultFlowDesignForm.progressBarTrackColor,
            progressBarFillColor:
              hex(flat?.progressBarFillColor ?? flat?.progressBarInnerColor) ??
              defaultFlowDesignForm.progressBarFillColor,
            translations: deepMergePlain(
              defaultFlowDesignForm.translations as unknown as Record<
                string,
                unknown
              >,
              (() => {
                const fromFlat = (flat as any)?.translations;
                const fromRoot = (data as any)?.translations;
                const raw =
                  typeof fromFlat === 'object' && fromFlat !== null
                    ? fromFlat
                    : typeof fromRoot === 'object' && fromRoot !== null
                      ? fromRoot
                      : undefined;
                return raw as Record<string, unknown> | undefined;
              })(),
            ) as FlowDesignTranslations,
            color: themeColor
              ? { ...defaultFlowDesignForm.color, ...themeColor }
              : defaultFlowDesignForm.color,
          });

          return {
            ...getInitialConfiguration(),
            flowName:
              data.flowName ?? data.name ?? data.layoutName ?? prev.flowName,
            customerid: data.customerId ?? data.customerid ?? prev.customerid,
            externalids: {
              incode:
                data.incodeClientId ??
                data.externalIncodeId ??
                data.externalids?.incode ??
                prev.externalids.incode,
            },
            documentTypes,
            design,
            checks: Array.isArray(data.checks) ? data.checks : prev.checks,
          };
        });
      })
      .catch(err => {
        if (!cancelled) {
          const message =
            err?.response?.data?.message ??
            err?.response?.data?.error ??
            err?.message ??
            'Failed to load flow';
          setLoadFlowError(String(message));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFlow(false);
      });
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  const { organizations, isLoading: isLoadingOrgs } = useAllOrganizations();
  const customers = useMemo(
    () =>
      organizations
        .filter(
          org => org.customerId != null && String(org.customerId).trim() !== '',
        )
        .map(org => ({
          id: org.customerId as string,
          name: org.display_name || org.name || org.customerId || org.id,
        })),
    [organizations],
  );

  //   const services = [
  //     { value: 'external.1', label: 'External Service 1' },
  //     { value: 'internal.1', label: 'Internal Service 1' },
  //   ];

  const buildCheckOptions = (): string[] => {
    const options: string[] = [];

    const hasPoa = selectedProducts.includes('poa');
    // Treat as IDV when explicitly selected, or when no wizard products were
    // provided (e.g. editing an existing flow) to preserve prior behavior.
    const hasIdv =
      selectedProducts.includes('idv') || selectedProducts.length === 0;

    if (hasIdv) {
      // Prefer an existing check option entry if present.
      const fromChecks = configuration.checks?.[0]?.checkoptions?.[0];
      if (Array.isArray(fromChecks) && fromChecks.length > 0) {
        // checkoptions is string[][], so this is the string[] inside it.
        options.push(fromChecks.join(','));
      } else {
        const dt = configuration.documentTypes;
        const docs = [
          dt.id && 'ID',
          dt.passport && 'PASSPORT',
          dt.residence && 'RESIDENCE',
          dt.drivingLicense && 'DRIVINGLICENSE',
        ].filter(Boolean) as string[];

        const provider = configuration.checks?.[0]?.provider || 'incode';
        const docList = docs.length ? docs.join(',') : 'ID,PASSPORT,RESIDENCE';
        options.push(`document:${docList}:${provider}`);
      }
    }

    if (hasPoa) {
      const poaDocs = [
        poaDocumentTypes?.invoice && 'INVOICE',
        poaDocumentTypes?.bill && 'BILL',
        poaDocumentTypes?.bank && 'BANK',
      ].filter(Boolean) as string[];
      const poaDocList = poaDocs.length
        ? poaDocs.join(',')
        : 'INVOICE,BILL,BANK';
      options.push(`document:${poaDocList}:sonio,incode+sonio`);
    }
    document: return options;
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    const customerName =
      customers.find(c => c.id === configuration.customerid)?.name ?? 'Flow';
    const now = new Date().toISOString().split('T')[0];
    const flowName =
      configuration.flowName?.trim() || `${customerName} - ${now}`;
    const dt = configuration.documentTypes;
    const documentTypes = [
      dt.id && 'ID',
      dt.passport && 'PASSPORT',
      dt.residence && 'RESIDENCE',
      dt.drivingLicense && 'DRIVINGLICENSE',
    ].filter(Boolean) as string[];

    const checkOptions = buildCheckOptions();

    const saveFlowDesign = async (targetFlowId: string) => {
      const formData = new FormData();
      appendFlowDesignFormData(formData, flowDesign, {
        checkOptions,
        documentTypes,
      });
      await axios.put(
        `${API_BASE_URL}/flows?flowId=${encodeURIComponent(targetFlowId)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    };

    try {
      if (flowId) {
        await saveFlowDesign(flowId);
        navigate('/flows/flow-list');
        return;
      }

      const payload = {
        customerId: configuration.customerid,
        name: flowName,
        externalIncodeId: configuration.externalids.incode || '',
        checkOptions,
      };

      const response = await axios.post(`${API_BASE_URL}/flows`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const apiFlowId =
        (response?.data &&
          (response.data.id || response.data.flowId || response.data._id)) ||
        null;
      if (!apiFlowId) {
        throw new Error('Flow created but no flow ID returned from API');
      }

      await saveFlowDesign(String(apiFlowId));

      const newFlow = {
        id: String(apiFlowId),
        name: flowName,
        description: `${configuration.checks.length} verification check(s)`,
        status: 'draft' as const,
        createdAt: now,
        updatedAt: now,
        nodesCount: configuration.checks.length,
        customerName: customerName,
        incodeClientId: configuration.externalids.incode || '',
        documentTypes,
      };

      const existingFlows = JSON.parse(localStorage.getItem('flows') || '[]');
      existingFlows.push(newFlow);
      localStorage.setItem('flows', JSON.stringify(existingFlows));
      localStorage.setItem(
        `flow-configuration-${newFlow.id}`,
        JSON.stringify(configuration),
      );
      if (configuration.design) {
        localStorage.setItem(
          `flow-design-${newFlow.id}`,
          JSON.stringify(configuration.design),
        );
      }

      navigate('/flows/flow-list');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to create flow';
      setSaveError(String(message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    const jsonString = JSON.stringify(configuration, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'flow-configuration.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-[#E5E7EB] p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/flows/flow-list')}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Back
              </Button>
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <SettingsIcon className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-[26px] font-extrabold tracking-[-0.025em] text-[#0C2340]">
                  Flow Configuration
                  {flowId && (
                    <span className="ml-2 text-base font-normal text-[#5B6B80]">
                      (editing)
                    </span>
                  )}
                </h1>
                <p className="text-sm text-[#6B6B6B]">
                  {loadingFlow
                    ? 'Loading flow details…'
                    : 'Configure verification flow settings and checks'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {loadFlowError && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ maxWidth: 320 }}
                >
                  {loadFlowError}
                </Typography>
              )}
              {saveError && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ maxWidth: 360 }}
                >
                  {saveError}
                </Typography>
              )}
              <Button
                variant="outlined"
                startIcon={<InfoIcon />}
                onClick={handleExport}
                disabled={isSaving || loadingFlow}
              >
                Export JSON
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || loadingFlow}
                className="!text-white bg-gradient-to-r from-[#00387D] to-[#002A5F] hover:from-[#002A5F] hover:to-[#00387D]"
              >
                {isSaving ? 'Saving…' : 'Save Configuration'}
              </Button>
            </div>
          </div>
        </div>

        {/* Configuration Form + Preview */}
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Grid container spacing={4}>
              {/* Basic Settings */}
              <Grid item xs={12}>
                <Card className="h-fit">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                      Basic Settings
                    </Typography>
                    <Grid container spacing={4} sx={{ mt: 0 }}>
                      {/* Left: Customer ID, Incode Client ID, Flow Name */}
                      <Grid item xs={12} lg={6}>
                        <div className="space-y-4">
                          <FormControl
                            fullWidth
                            disabled={!!flowId || isLoadingOrgs}
                          >
                            <InputLabel shrink>Customer ID</InputLabel>
                            <Select
                              value={
                                configuration.customerid &&
                                customers.some(
                                  c => c.id === configuration.customerid,
                                )
                                  ? configuration.customerid
                                  : customers.length === 1
                                    ? customers[0].id
                                    : ''
                              }
                              label="Customer ID"
                              onChange={e =>
                                setConfiguration(prev => ({
                                  ...prev,
                                  customerid: e.target.value,
                                }))
                              }
                              displayEmpty
                              renderValue={v => {
                                if (!v)
                                  return isLoadingOrgs
                                    ? 'Loading…'
                                    : 'Select customer';
                                return (
                                  customers.find(c => c.id === v)?.name ?? v
                                );
                              }}
                            >
                              {isLoadingOrgs ? (
                                <MenuItem value="" disabled>
                                  Loading…
                                </MenuItem>
                              ) : customers.length === 0 ? (
                                <MenuItem value="" disabled>
                                  No organizations with customer ID
                                </MenuItem>
                              ) : (
                                customers.map(customer => (
                                  <MenuItem
                                    key={customer.id}
                                    value={customer.id}
                                  >
                                    {customer.name}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                          <TextField
                            fullWidth
                            label="Incode Client ID"
                            value={configuration.externalids.incode}
                            onChange={e =>
                              setConfiguration(prev => ({
                                ...prev,
                                externalids: {
                                  ...prev.externalids,
                                  incode: e.target.value,
                                },
                              }))
                            }
                            disabled={!!flowId}
                          />
                          <TextField
                            fullWidth
                            label="Flow Name"
                            value={configuration.flowName}
                            onChange={e =>
                              setConfiguration(prev => ({
                                ...prev,
                                flowName: e.target.value,
                              }))
                            }
                            placeholder="Enter flow name"
                          />
                        </div>
                      </Grid>
                      {/* Right: Document types */}
                      <Grid item xs={12} lg={6}>
                        <Box sx={{ height: '100%' }}>
                          <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            Document types
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: 1,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={configuration.documentTypes.id}
                                  onChange={e =>
                                    setConfiguration(prev => ({
                                      ...prev,
                                      documentTypes: {
                                        ...prev.documentTypes,
                                        id: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="ID"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={configuration.documentTypes.passport}
                                  onChange={e =>
                                    setConfiguration(prev => ({
                                      ...prev,
                                      documentTypes: {
                                        ...prev.documentTypes,
                                        passport: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="PASSPORT"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    configuration.documentTypes.residence
                                  }
                                  onChange={e =>
                                    setConfiguration(prev => ({
                                      ...prev,
                                      documentTypes: {
                                        ...prev.documentTypes,
                                        residence: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="RESIDENCE"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    configuration.documentTypes.drivingLicense
                                  }
                                  onChange={e =>
                                    setConfiguration(prev => ({
                                      ...prev,
                                      documentTypes: {
                                        ...prev.documentTypes,
                                        drivingLicense: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="DRIVINGLICENSE"
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Flow Design - under Basic Settings */}
              <Grid item xs={12}>
                <FlowCustomContent
                  design={flowDesign}
                  previewDocumentTypes={configuration.documentTypes}
                  onDesignChange={updates => {
                    setFlowDesign(prev => {
                      const { translations: ut, ...rest } = updates;
                      // Detect banner removal (no new file and no URL) vs new upload.
                      const bannerRemoved =
                        'bannerImg' in updates || 'bannerImageFile' in updates
                          ? !rest.bannerImageFile && !rest.bannerImg
                          : prev.bannerRemoved;
                      const next = {
                        ...prev,
                        ...rest,
                        bannerRemoved,
                        ...(ut
                          ? {
                              translations: deepMergePlain(
                                prev.translations as unknown as Record<
                                  string,
                                  unknown
                                >,
                                ut as unknown as Record<string, unknown>,
                              ) as FlowDesignTranslations,
                            }
                          : {}),
                      };
                      if (JSON.stringify(next) === JSON.stringify(prev))
                        return prev;
                      return next;
                    });
                    setConfiguration(prev => {
                      // Build next config
                      const nextConfig = {
                        ...prev,
                        design: {
                          ...prev.design,
                          ...(updates.translations
                            ? {
                                translations: deepMergePlain(
                                  (prev.design as any).translations ?? {},
                                  updates.translations as unknown as Record<
                                    string,
                                    unknown
                                  >,
                                ),
                              }
                            : {}),
                          appBar: {
                            ...prev.design.appBar,
                            ...(updates.appBarClass
                              ? { appBarClass: `bg-[${updates.appBarClass}]` }
                              : {}),
                            ...(updates.appBarButtonColor
                              ? { appBarButtonColor: updates.appBarButtonColor }
                              : {}),
                          },
                          theme: {
                            ...prev.design.theme,
                            ...(updates.buttonBgColor || updates.buttonTextColor
                              ? {
                                  buttonClass: `rounded-full text-[${updates.buttonTextColor ?? prev.design.theme.buttonClass}] mb-6 py-3 px-8 font-semibold bg-[${updates.buttonBgColor ?? extractBgHex(prev.design.theme.buttonClass) ?? '#00387D'}]`,
                                }
                              : {}),
                            ...(updates.secondaryButtonColor
                              ? {
                                  secondaryButtonClass: `text-center flex m-auto text-[15px] text-[${updates.secondaryButtonColor}]`,
                                }
                              : {}),
                            ...(updates.documentLinkBgColor
                              ? {
                                  documentLinkClass: `bg-[${updates.documentLinkBgColor}] border-solid border-2 rounded-lg`,
                                }
                              : {}),
                            ...(updates.backgroundColor
                              ? { backgroundColor: updates.backgroundColor }
                              : {}),
                            ...(updates.textColor
                              ? { textColor: updates.textColor }
                              : {}),
                            ...(updates.color
                              ? {
                                  color: {
                                    ...prev.design.theme.color,
                                    ...updates.color,
                                  },
                                }
                              : {}),
                          },
                        },
                      };
                      if (JSON.stringify(nextConfig) === JSON.stringify(prev))
                        return prev;
                      return nextConfig;
                    });
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default FlowConfiguration;
